/**
 * Utility functions for calendar store optimistic updates
 */

/**
 * Updates the project/phase assignment for a timeline in the employee data (optimistic update)
 *
 * @param employees - The employees resources array from calendar data
 * @param projects - The projects resources array from calendar data
 * @param timespanId - The timeline/assignment ID to update
 * @param employeeId - The employee ID (null for team assignments)
 * @param teamId - The team ID (null for individual employee assignments)
 * @param projectId - The new project ID to assign
 * @param phaseId - The new phase ID to assign (optional)
 */
export function updateTimelineProjectAssignment(
  employees: any[],
  projects: any[],
  timespanId: string,
  employeeId: string | number | null,
  teamId: string | number | null,
  projectId: string | number,
  phaseId: string | number | null
): void {
  // Find the project and phase details for display
  const project = projects.find((p: any) => p.id === projectId)
  const phase = phaseId && project?.phases ? project.phases.find((ph: any) => ph.id === phaseId) : null

  // Update the timeline in the local state
  for (const employee of employees) {
    if (employee.type === "SINGLE" && employee.id === employeeId) {
      // Single employee
      const timeline = employee.childs?.find((c: any) => c.id === timespanId)
      if (timeline) {
        timeline.projectId = projectId
        timeline.phase = phaseId ? { id: phaseId } : null
        timeline.name = phase ? phase.name : project?.name || timeline.name
        timeline.color = phase?.color || project?.color || timeline.color
      }
    } else if (employee.type === "MULTIPLE" && employee.id === teamId) {
      // Team - find the specific team member
      for (const teamMember of employee.assignedEmployees || []) {
        if (teamMember.id === employeeId) {
          const timeline = teamMember.childs?.find((c: any) => c.id === timespanId)
          if (timeline) {
            timeline.projectId = projectId
            timeline.phase = phaseId ? { id: phaseId } : null
            timeline.name = phase ? phase.name : project?.name || timeline.name
            timeline.color = phase?.color || project?.color || timeline.color
          }
        }
      }
    }
  }
}

/**
 * Updates the start/end dates of a timeline in the employee data (optimistic update)
 *
 * @param employees - The employees resources array from calendar data
 * @param timespanId - The timeline/assignment ID to update
 * @param employeeId - The employee ID (null for team assignments)
 * @param teamId - The team ID (null for individual employee assignments)
 * @param startDate - The new start date (ISO date string)
 * @param endDate - The new end date (ISO date string)
 */
export function updateTimelineSpan(
  employees: any[],
  timespanId: string,
  employeeId: string | number | null,
  teamId: string | number | null,
  startDate: string,
  endDate: string
): void {
  for (const employee of employees) {
    if (employee.type === "SINGLE" && employee.id === employeeId) {
      // Single employee
      const timeline = employee.childs?.find((c: any) => c.id === timespanId)
      if (timeline) {
        timeline.from = startDate
        timeline.to = endDate
      }
    } else if (employee.type === "MULTIPLE" && employee.id === teamId) {
      // Team - the assignment belongs to exactly one member, match by timespan id
      for (const teamMember of employee.assignedEmployees || []) {
        const timeline = teamMember.childs?.find((c: any) => c.id === timespanId)
        if (timeline) {
          timeline.from = startDate
          timeline.to = endDate
        }
      }
    }
  }
}

/**
 * Updates the start/end dates of a project or phase in the project data (optimistic update)
 * and keeps mostStartDate/mostEndDate consistent, mirroring the backend mapper.
 *
 * @param projectsData - The projects object from calendar data ({ mostStartDate, mostEndDate, resources })
 * @param projectId - The project ID to update
 * @param phaseId - The phase ID to update (null to update the project itself)
 * @param startDate - The new start date (ISO date string)
 * @param endDate - The new end date (ISO date string)
 */
export function updateProjectSpanLocal(
  projectsData: any,
  projectId: string | number,
  phaseId: string | number | null,
  startDate: string,
  endDate: string
): void {
  const project = projectsData?.resources?.find((p: any) => p.id === projectId)
  if (!project) return

  if (phaseId) {
    const phase = project.phases?.find((ph: any) => ph.id === phaseId)
    if (phase) {
      phase.start = startDate
      phase.end = endDate
    }
  } else {
    project.start = startDate
    project.end = endDate
  }

  // Recompute the overall span like the backend does (project dates only)
  const starts = projectsData.resources.map((p: any) => p.start).filter(Boolean)
  const ends = projectsData.resources.map((p: any) => p.end).filter(Boolean)
  if (starts.length) projectsData.mostStartDate = starts.reduce((a: string, b: string) => (a < b ? a : b))
  if (ends.length) projectsData.mostEndDate = ends.reduce((a: string, b: string) => (a > b ? a : b))
}

/**
 * Removes a timeline assignment from the employee data (optimistic update)
 *
 * @param employees - The employees resources array from calendar data
 * @param timespanId - The timeline/assignment ID to remove
 * @param employeeId - The employee ID (null for team assignments)
 * @param teamId - The team ID (null for individual employee assignments)
 */
export function removeTimelineAssignment(
  employees: any[],
  timespanId: string,
  employeeId: string | number | null,
  teamId: string | number | null
): void {
  // Remove the timeline from the local state
  for (const employee of employees) {
    if (employee.type === "SINGLE" && employee.id === employeeId) {
      // Single employee
      if (employee.childs) {
        employee.childs = employee.childs.filter((c: any) => c.id !== timespanId)
      }
    } else if (employee.type === "MULTIPLE" && employee.id === teamId) {
      // Team - find the specific team member
      for (const teamMember of employee.assignedEmployees || []) {
        if (teamMember.id === employeeId) {
          if (teamMember.childs) {
            teamMember.childs = teamMember.childs.filter((c: any) => c.id !== timespanId)
          }
        }
      }
    }
  }
}
